import { SectionHeader } from '../common/section-header'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useClient } from '../clients/use-client'
import { keysStaleAfterStudentChange } from '../api/query-keys'
import { SaveButton } from '../common/save-button'
import { Notice, NoticeAction } from '@speaking-coach/ui'
import { CompanyInformationFields, companyInformationShape } from './company-information-fields'
import { adminStudentClient, messageForStudentError } from './api'
import type { StudentRow } from './types'

const schema = z.object(companyInformationShape)
const valuesFor = (student: StudentRow) => ({
  clientEmployeeId: student.clientEmployeeId ?? '',
  clientDepartment: student.clientDepartment ?? '',
})

export function CompanyInformationForm({ student, onChange, onDirty }: {
  student: StudentRow
  onChange: (student: StudentRow) => void
  onDirty: (dirty: boolean) => void
}): JSX.Element {
  const client = useClient(student.clientId)
  const cache = useQueryClient()
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: valuesFor(student),
  })
  const dirty = form.formState.isDirty
  useEffect(() => {
    onDirty(dirty)
    return () => onDirty(false)
  }, [dirty, onDirty])
  const save = useMutation({
    mutationFn: (values: z.infer<typeof schema>) =>
      adminStudentClient.updateCompanyInformation(student.clientId, student.userId, {
        ...(client.data?.clientEmployeeIdEnabled && form.formState.dirtyFields.clientEmployeeId
          ? { clientEmployeeId: values.clientEmployeeId || null } : {}),
        ...(client.data?.clientDepartmentEnabled && form.formState.dirtyFields.clientDepartment
          ? { clientDepartment: values.clientDepartment || null } : {}),
      }),
    onSuccess: (updated) => {
      form.reset(valuesFor(updated))
      onChange(updated)
      for (const queryKey of keysStaleAfterStudentChange(student.clientId)) {
        void cache.invalidateQueries({ queryKey })
      }
    },
  })
  const employeeEnabled = client.data?.clientEmployeeIdEnabled === true
  const departmentEnabled = client.data?.clientDepartmentEnabled === true
  const settingsLoaded = client.isSuccess && !!client.data
  return (
    <form
      aria-label="Company information"
      className="bg-surface-card rounded-token-card p-6 space-y-4"
      noValidate
      onSubmit={form.handleSubmit((values) => {
        if (dirty && !save.isPending) save.mutate(values)
      })}
    >
      <SectionHeader
        title="Company information"
        description={<>These references belong to the client's employee records. Each field is enabled
          independently in <Link className="underline" to={`/admin/clients/${student.clientId}`}>client settings</Link>.
          Disabled fields retain their saved values and are hidden from HR.
        </>}
      />
      {client.isError ? (
        <Notice variant="danger" action={<NoticeAction onClick={() => void client.refetch()}>Retry</NoticeAction>}>
          Could not load company information settings. Saved values are shown below; editing is unavailable until settings load.
        </Notice>
      ) : !settingsLoaded ? (
        <p className="text-body text-text-secondary">{client.isPending ? 'Loading client settings…' : 'Client settings are unavailable.'}</p>
      ) : null}
      <CompanyInformationFields
        showAvailability={settingsLoaded}
        employee={form.register('clientEmployeeId')}
        employeeDisabled={!employeeEnabled}
        department={form.register('clientDepartment')}
        departmentDisabled={!departmentEnabled}
        employeeError={form.formState.errors.clientEmployeeId?.message}
        departmentError={form.formState.errors.clientDepartment?.message}
      />
      {save.error && <Notice variant="danger">{messageForStudentError(save.error)}</Notice>}
      {(employeeEnabled || departmentEnabled) && <SaveButton
        pending={save.isPending}
        dirty={dirty}
        justSaved={save.isSuccess}
        disabled={!dirty || save.isPending}
      />}
    </form>
  )
}
